import { RequestClientService, ResponseType } from "@rightship-tech/common-ui/services/basic/request-client.service";
import {
    GetVesselRequest,
    GetVesselResponse,
    GetSafetyRatingResponse,
    RatingsSummaryModel,
    ActivitiesData,
    GetBannerResponse,
    GetVesselPerformance,
    VesselCertificateModel,
    GetSubscoreRatingsModelResponse,
    SubscoreIncidentDataModelResponse,
    SubscorePscInspectionDataModelResponse,
    GetVesselBenchmarkResponse,
    GetSafetyScoreChangeModelResponse,
    GetModelLastRunResponse,
    GetVesselGhgSummary,
    VesselCapSurveyClientModel,
    SaveCAPSurveyRequest,
    DeleteCAPSurveyResponse,
    SaveCAPSurveyResponse,
    VesselThirdPartyInspectionClientModel,
    SaveThirdPartyInspectionRequest,
    SaveThirdPartyInspectionResponse,
    DeleteThirdPartyInspectionResponse,
    GetVesselGHGRatingPeerGroupResponse,
    GetVesselApplicationFormIdFromGuidRequest,
    GetVesselApplicationFormIdFromGuidResponse,
    ClearVesselMouResponse,
    VesselMousEnum,
    ClearVesselMouRequest,
    GetVesselMousResponse,
    GetVesselMousRequest,
    GetVesselClassHistoryResponse,
    VesselHeaderModel,
    GetVesselSurveysAndCertificatesResponse,
    GetVesselUscgResponse,
    GetVesselTypesGHGRatingPeerGroupModel,
    SaveVesselRequest,
    GetVesselOperationalMetricsResponse,
    VesselClassSurveyClientModel,
    SaveClassSurveyRequest,
    SaveClassSurveyResponse,
    DeleteClassSurveyResponse,
    VesselClassCertificateClientModel,
    SaveClassCertificateRequest,
    SaveClassCertificateResponse,
    DeleteClassCertificateResponse,
    GetVesselAnonymouslyResponse,
    GetVesselDynamicPropertiesResponse,
    CloseGracePeriodBannerRequest,
    GetVesselsByCompanyIdResponse,
    ExtractVesselsFromFileRequest,
    ExtractVesselsFromFileResponse
} from "../../models/vessels/vessel.models";
import { VesselRoutes } from "../../constants/vessels/vessels.constants";
import { autoinject } from "aurelia-framework";
import { EntityDynamicValue } from "../../models/basics/basics.models";
import { GetDynamicPropertiesResponse } from "../../models/manage/entities.models";
import { BaseValidationResponse } from "../../models/validations/validations.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../constants/basics/rightship-dialog.constants";
import { ChooseVetScreeningDialogModel } from "../../views/pages/modules/vessels/dialogs/choose-vet-screening-dialog";
import { RulesRoutes } from "../../constants/rules/rules.constants";
import { VettingType } from "../../models/vetting/vetting.models";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";

@autoinject
export class ManageVesselService {

    async showChooseDialog(model: ChooseVetScreeningDialogModel) {
        const showChooseDialog = () => this.dialogService.show(RightShipDialogServiceConstants.URLs.ChooseVetScreeningDialog, model, true, false, false, 'right');
        if (model.vettingType == VettingType.FullVet) {
            const businessUnitId = await this.customerService.getBusinessUnitId();
            if (businessUnitId && model.vettingType == VettingType.FullVet) {
                try {
                    const ruleCollections = await this.requestClient.get<any[]>(`${RulesRoutes.GetBuRuleCollections}/${businessUnitId}`, {});
                    if (ruleCollections && ruleCollections.filter(x => x.vettingTypeId == VettingType.FullVet).length == 1) {
                        model["vetRequiresMoreInformationEmails"] = model.vessel.vetRequiresMoreInformationEmails;
                        const ruleCollectionId = ruleCollections.filter(x => x.vettingTypeId == VettingType.FullVet)[0].ruleCollectionId;
                        model["ruleCollectionId"] = ruleCollectionId;
                        this.dialogService.show(RightShipDialogServiceConstants.URLs.RequestVetDialog, model, true, false, false, 'right');
                    }
                    else showChooseDialog();
                }
                catch (e) {
                    console.error("Error getting rule collections", e);
                    showChooseDialog();
                }
            }
            else showChooseDialog();
        }
        else showChooseDialog();
    }

    getSafetyRatingModelCache: { [index: number]: GetSafetyRatingResponse } = {};
    getSafetyRatingModelPromiseCache: { [index: number]: Promise<GetSafetyRatingResponse> } = {};

    constructor(private requestClient: RequestClientService, private dialogService: DialogClientService, private customerService: CommonCustomerService) { }

    public rerunSystemRules(vesselIds: number[]): Promise<void> {
        return this.requestClient.post(`${VesselRoutes.ReRunSystemRules}`, {}, { vesselIds });
    }

    public getVesselApplicationFormCorrespondenceCount(vesselApplicationFormId: number): Promise<number> {
        return this.requestClient.get(`${VesselRoutes.GetVesselApplicationFormCorrespondenceCount}/${vesselApplicationFormId}`, {});
    }

    public getVessel(request: GetVesselRequest): Promise<GetVesselResponse> {
        return this.requestClient.get(VesselRoutes.GetVessel, request);
    }

    public getVesselApplicationFormIdFromGuid(request: GetVesselApplicationFormIdFromGuidRequest): Promise<GetVesselApplicationFormIdFromGuidResponse> {
        return this.requestClient.get(VesselRoutes.GetVesselApplicationFormIdFromGuid, request);
    }

    public getVesselAnonymously(request: GetVesselRequest): Promise<GetVesselAnonymouslyResponse> {
        return this.requestClient.get(VesselRoutes.GetVesselAnonymously, request);
    }

    public saveDynamicPropertiesForEntity(entityDynamicValues: EntityDynamicValue[], isAudit: boolean = false): Promise<GetDynamicPropertiesResponse> {
        return this.requestClient.post<GetDynamicPropertiesResponse>(VesselRoutes.SaveDynamicPropertiesForEntity, {}, { entityDynamicValues, isAudit });
    }

    async getSafetyRatingModel(vesselId: number) {
        if (this.getSafetyRatingModelCache[vesselId])
            return this.getSafetyRatingModelCache[vesselId];

        if (this.getSafetyRatingModelPromiseCache[vesselId])
            return await this.getSafetyRatingModelPromiseCache[vesselId];

        this.getSafetyRatingModelPromiseCache[vesselId] = this.requestClient.get(`${VesselRoutes.GetSafetyRating}/${vesselId}`, {});

        this.getSafetyRatingModelCache[vesselId] = await this.getSafetyRatingModelPromiseCache[vesselId];
        return this.getSafetyRatingModelCache[vesselId];
    }

    public clearSafetyRatingModelCache(vesselId: number) {
        this.getSafetyRatingModelPromiseCache[vesselId] = null;
        this.getSafetyRatingModelCache[vesselId] = null;
    }

    public getSafetyScoreChangeModel(safetyScoreId: number): Promise<GetSafetyScoreChangeModelResponse> {
        return this.requestClient.get(`${VesselRoutes.GetSafetyScoreChange}/${safetyScoreId}`, {});
    }

    public getModelLastRun(vesselId: number): Promise<GetModelLastRunResponse> {
        return this.requestClient.get(`${VesselRoutes.GetModelLastRun}/${vesselId}`, {});
    }

    public getVesselBenchmark(vesselId: number): Promise<GetVesselBenchmarkResponse[]> {
        return this.requestClient.get(`${VesselRoutes.GetVesselBenchmark}/${vesselId}`, {});
    }

    public getRatingsModel(vesselId: number): Promise<RatingsSummaryModel> {
        return this.requestClient.get(`${VesselRoutes.GetRatingsModel}/${vesselId}`, {});
    }

    public getSubscoreRatingsModel(vesselId: number, dataTypeId: number, entityId: number): Promise<GetSubscoreRatingsModelResponse> {
        return this.requestClient.get(`${VesselRoutes.GetSafetySubscoreModel}`, {
            vesselId: vesselId,
            dataTypeId: dataTypeId,
            entityId: entityId
        });
    }

    public getSafetyVesselSubscoreIncidentModel(vesselId: number): Promise<SubscoreIncidentDataModelResponse> {
        return this.requestClient.get(`${VesselRoutes.GetSafetyVesselSubscoreIncidentModel}/${vesselId}`, {});
    }

    public getSafetyVesselSubscorePscInspectionModel(vesselId: number): Promise<SubscorePscInspectionDataModelResponse> {
        return this.requestClient.get(`${VesselRoutes.GetSafetyVesselSubscorePscInspectionModel}/${vesselId}`, {});
    }

    public getVesselActivitiesData(vesselId: number, from: number, to: number): Promise<ActivitiesData[]> {
        return this.requestClient.get(`${VesselRoutes.GetVesselActivitiesData}/${vesselId}/${from}/${to}`, {});
    }

    public getVesselBanners(vesselId: number): Promise<GetBannerResponse[]> {
        return this.requestClient.get(`${VesselRoutes.GetBanners}/${vesselId}`, {});
    }

    public getVesselPerformance(vesselId: number): Promise<GetVesselPerformance> {
        return this.requestClient.get(`${VesselRoutes.GetVesselPerformance}/${vesselId}`, {});
    }

    vesselHeaderPromiseCache: { [index: number]: Promise<VesselHeaderModel> } = {};
    vesselHeaderCache: { [index: number]: VesselHeaderModel } = {};

    public async getVesselHeader(vesselId: number, force: boolean): Promise<VesselHeaderModel> {
        if (!force && this.vesselHeaderCache[vesselId]) return this.vesselHeaderCache[vesselId];

        if (force || !this.vesselHeaderPromiseCache[vesselId])
            this.vesselHeaderPromiseCache[vesselId] = this.requestClient.get(`${VesselRoutes.GetVesselHeader}/${vesselId}`, {});
        this.vesselHeaderCache[vesselId] = await this.vesselHeaderPromiseCache[vesselId];
        return this.vesselHeaderCache[vesselId];
    }

    public getVesselGhgSummary(vesselId: number): Promise<GetVesselGhgSummary> {
        return this.requestClient.get(`${VesselRoutes.GetVesselGhgSummary}/${vesselId}`, {});
    }

    public getVesselTypesGHGRatingPeerGroup(vesselTypeEEDIGroupCode?: string): Promise<GetVesselTypesGHGRatingPeerGroupModel[]> {
        return this.requestClient.get(`${VesselRoutes.GetVesselTypesGHGRatingPeerGroup}/${vesselTypeEEDIGroupCode}`, {});
    }

    public getVesselOperationalMetrics(vesselId: number): Promise<GetVesselOperationalMetricsResponse> {
        return this.requestClient.get(`${VesselRoutes.GetVesselOperationalMetrics}/${vesselId}`, {});
    }

    public getVesselGHGRatingPeerGroup(vesselId: number): Promise<GetVesselGHGRatingPeerGroupResponse> {
        return this.requestClient.get(`${VesselRoutes.GetVesselGHGRatingPeerGroup}/${vesselId}`, {});
    }

    public getSiblingsPdf(vesselId: number, leadSisterVesselId?: number): Promise<void> {
        const uri = leadSisterVesselId ? `${VesselRoutes.DownloadPdf}?vesselId=${vesselId}&leadSisterVesselId=${leadSisterVesselId}` : `${VesselRoutes.DownloadPdf}?vesselId=${vesselId}`;

        return this.requestClient.get<Blob>(uri, {}, ResponseType.Blob).then(x => {
            this.requestClient.downloadFile(x, "Vessel Siblings" + ".pdf");
        }, e => { throw e; });
    }

    public getSiblingsExcel(vesselId: number, leadSisterVesselId?: number): Promise<void> {
        const uri = leadSisterVesselId ? `${VesselRoutes.DownloadExcel}?vesselId=${vesselId}&leadSisterVesselId=${leadSisterVesselId}` : `${VesselRoutes.DownloadExcel}?vesselId=${vesselId}`;

        return this.requestClient.get<Blob>(uri, {}, ResponseType.Blob).then(x => {
            this.requestClient.downloadFile(x, "Vessel Siblings" + ".xlsx");
        }, e => { throw e; });
    }

    public downloadVesselDetailsAsExcel(vesselId: number): Promise<void> {
        const uri = `${VesselRoutes.DownloadVesselDetailsAsExcel}?vesselId=${vesselId}`;

        return this.requestClient.get<Blob>(uri, {}, ResponseType.Blob).then(x => {
            this.requestClient.downloadFile(x, "Vessel Details" + ".xlsx");
        }, e => { throw e; });
    }

    public sendVesselForm(request: { formId: number; emails: { email: string; emailRecipientTypeId: number; }[]; vesselId: number; message: string; }): Promise<string> {
        return this.requestClient.post(`${VesselRoutes.SendVesselForm}`, {}, {
            formId: request.formId,
            emails: request.emails,
            vesselId: request.vesselId,
            message: request.message
        });
    }

    public getVesselCertificates(vesselId: number): Promise<VesselCertificateModel[]> {
        return this.requestClient.get(VesselRoutes.GetVesselCertificates, { vesselId });
    }

    public getVesselUscgData(vesselId: number, loadLod: boolean, loadCotp: boolean): Promise<GetVesselUscgResponse> {
        return this.requestClient.post(VesselRoutes.GetVesselUscgData, {}, { vesselId: vesselId, includeLodData: loadLod, includeCotpData: loadCotp });
    }
    public getVesselSurveysAndCertificates(vesselId: number): Promise<GetVesselSurveysAndCertificatesResponse> {
        return this.requestClient.post(VesselRoutes.GetVesselSurveysAndCertificates, {}, { vesselId: vesselId, includeClassSurveys: true, includeClassCertificates: true });
    }

    public getCAPSurvey(capSurveyId: number): Promise<VesselCapSurveyClientModel> {
        return this.requestClient.get(VesselRoutes.GetCAPSurvey, { id: capSurveyId });
    }

    public saveCAPSurvey(request: SaveCAPSurveyRequest): Promise<SaveCAPSurveyResponse> {
        return new Promise<SaveCAPSurveyResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.SaveCAPSurvey, {}, request).then((response: SaveCAPSurveyResponse) => {
                if (response.validationResult && response.validationResult.hasError) {
                    reject(response);
                } else {
                    resolve(response);
                }
            }, (e) => {
                reject(e);
            });
        });
    }

    public deleteCAPSurvey(capSurveyId: number): Promise<DeleteCAPSurveyResponse> {
        return new Promise<DeleteCAPSurveyResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.DeleteCAPSurvey, {}, { vesselCapSurveyId: capSurveyId }).then((response: DeleteCAPSurveyResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public getVesselThirdPartyInspections(vesselId: number): Promise<VesselThirdPartyInspectionClientModel[]> {
        return this.requestClient.post(VesselRoutes.GetVesselThirdPartyInspections, {}, { vesselId: vesselId });
    }

    public getThirdPartyInspection(thirdPartyInspectionId: number): Promise<VesselThirdPartyInspectionClientModel> {
        return this.requestClient.get(VesselRoutes.GetThirdPartyInspection, { id: thirdPartyInspectionId });
    }

    public saveThirdPartyInspection(request: SaveThirdPartyInspectionRequest): Promise<SaveThirdPartyInspectionResponse> {
        return this.requestClient.post(VesselRoutes.SaveThirdPartyInspection, {}, request);
    }

    public deleteThirdPartyInspection(thirdPartyInspectionId: number): Promise<DeleteThirdPartyInspectionResponse> {
        return new Promise<DeleteThirdPartyInspectionResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.DeleteThirdPartyInspection, {}, { vesselThirdPartyInspectionId: thirdPartyInspectionId }).then((response: DeleteThirdPartyInspectionResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public matchVessels(survivingVesselId: number, vesselsToDelete: number[]): Promise<any> {
        return this.requestClient.post(VesselRoutes.MatchVessels, {}, {
            survivingVesselId,
            vesselsToDelete
        });
    }

    public getVesselMous(vesselId: number): Promise<GetVesselMousResponse> {
        return new Promise<GetVesselMousResponse>((resolve, reject) => {
            const request: GetVesselMousRequest = { vesselId: vesselId };
            this.requestClient.post(VesselRoutes.GetVesselMous, {}, request).then((response: GetVesselMousResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public clearVesselMou(vesselId: number, mou: VesselMousEnum = null): Promise<ClearVesselMouResponse> {
        return new Promise<ClearVesselMouResponse>((resolve, reject) => {
            const request: ClearVesselMouRequest = { vesselId: vesselId, mou: mou };
            this.requestClient.post(VesselRoutes.ClearVesselMou, {}, request).then((response: ClearVesselMouResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public markUscgItemAsClosed(activityId: number, isLod: boolean, comments: string) {
        return new Promise<void>((resolve, reject) => {
            const request = { activityId: activityId, isLod: isLod, comments: comments };
            this.requestClient.post(VesselRoutes.MarkUscgItemAsClosed, {}, request).then(() => {
                resolve(null);
            }, (e) => {
                reject(e);
            });
        });
    }

    public getClassSocietiesHistories(vesselId: number): Promise<GetVesselClassHistoryResponse> {
        return this.requestClient.get(VesselRoutes.GetClassSocietiesHistories, { vesselId: vesselId });
    }

    public saveVessel(request: SaveVesselRequest): Promise<BaseValidationResponse> {
        return new Promise<SaveCAPSurveyResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.SaveVessel, {}, request).then((response: SaveCAPSurveyResponse) => {
                if (response.validationResult && response.validationResult.hasError) {
                    reject(response);
                } else {
                    resolve(response);
                }
            }, (e) => {
                reject(e);
            });
        });
    }

    public getClassSurvey(classSurveyId: number): Promise<VesselClassSurveyClientModel> {
        return this.requestClient.get(VesselRoutes.GetClassSurvey, { id: classSurveyId });
    }

    public saveClassSurvey(request: SaveClassSurveyRequest): Promise<SaveClassSurveyResponse> {
        return new Promise<SaveClassSurveyResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.SaveClassSurvey, {}, request).then((response: SaveClassSurveyResponse) => {
                if (response.validationResult && response.validationResult.hasError) {
                    reject(response);
                } else {
                    resolve(response);
                }
            }, (e) => {
                reject(e);
            });
        });
    }

    public deleteClassSurvey(classSurveyId: number, dontShowWarningAgain: boolean): Promise<DeleteClassSurveyResponse> {
        return new Promise<DeleteClassSurveyResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.DeleteClassSurvey, {}, { vesselClassSurveyId: classSurveyId, dontShowWarningAgain: dontShowWarningAgain }).then((response: DeleteClassSurveyResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public getClassCertificate(classCertificateId: number): Promise<VesselClassCertificateClientModel> {
        return this.requestClient.get(VesselRoutes.GetClassCertificate, { id: classCertificateId });
    }

    public saveClassCertificate(request: SaveClassCertificateRequest): Promise<SaveClassCertificateResponse> {
        return new Promise<SaveClassCertificateResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.SaveClassCertificate, {}, request).then((response: SaveClassCertificateResponse) => {
                if (response.validationResult && response.validationResult.hasError) {
                    reject(response);
                } else {
                    resolve(response);
                }
            }, (e) => {
                reject(e);
            });
        });
    }

    public deleteClassCertificate(classCeritificateId: number, dontShowWarningAgain: boolean): Promise<DeleteClassCertificateResponse> {
        return new Promise<DeleteClassCertificateResponse>((resolve, reject) => {
            this.requestClient.post(VesselRoutes.DeleteClassCertificate, {}, { vesselClassCertificateId: classCeritificateId, dontShowWarningAgain: dontShowWarningAgain }).then((response: DeleteClassCertificateResponse) => {
                resolve(response);
            }, (e) => {
                reject(e);
            });
        });
    }

    public getVesselDynamicProperties(vesselId: number): Promise<GetVesselDynamicPropertiesResponse> {
        return this.requestClient.get<GetVesselDynamicPropertiesResponse>(VesselRoutes.GetVesselDynamicProperties, { vesselId });
    }

    public canVesselBeVetted(vesselId: number): Promise<boolean> {
        return this.requestClient.get<boolean>(`${VesselRoutes.CanVesselBeVetted}/${vesselId}`, {});
    }

    public getMaskDataForVessel(vesselId: number): Promise<boolean> {
        return this.requestClient.get(`${VesselRoutes.GetMaskDataForVessel}/${vesselId}`, {});
    }

    public closeGracePeriodBanner(request: CloseGracePeriodBannerRequest): Promise<void> {
        return this.requestClient.post(VesselRoutes.CloseGracePeriodBanner, {}, request);
    }

    public extractVesselsFromFile(request: ExtractVesselsFromFileRequest): Promise<ExtractVesselsFromFileResponse> {
        return this.requestClient.postFormData(VesselRoutes.ExtractVesselsFromFile, request);
    }

    public getVesselsByCompanyId(companyId: number): Promise<GetVesselsByCompanyIdResponse> {
        return this.requestClient.get<GetVesselsByCompanyIdResponse>(`${VesselRoutes.GetVesselsByCompanyId}/${companyId}`, {});
    }
}
